

export default getBundle;

import { relative, resolve, sep } from 'path';
import hasOwnProp from 'utils/hasOwnProp';
import resolveId from 'utils/resolveId';
import promiseSequence from 'utils/promiseSequence';
import sortModules from './utils/sortModules';
import resolveChains from './utils/resolveChains';
import combine from './combine';
import { readFile, stat, Promise } from 'sander';
import getModule from './getModule';
function getBundle(options) {
	var entry = options.entry.replace(/\.js$/, '');
	var userModules = options.modules || {};
	var modules = [];
	var moduleLookup = {};
	var promiseByPath = {};
	var skip = options.skip;
	var names = options.names;
	var base = (options.base ? resolve(options.base) : process.cwd()) + '/';
	var externalModules = [];
	var externalModuleLookup = {};

	if (!entry.indexOf(base)) {
		entry = entry.substring(base.length);
	}

	// resolve user module paths
	options.modules && Object.keys(options.modules).forEach(function (relativePath) {
		userModules[resolve(base, relativePath)] = options.modules[relativePath];
	});

	var cyclicalModules = [];

	return resolvePath(base, userModules, entry, null).then(function (absolutePath) {
		return fetchModule(entry, absolutePath).then(function (entryModule) {
			return Promise.all(cyclicalModules).then(function () {
				// if the bundle contains cyclical modules,
				// we may need to sort it again
				if (cyclicalModules.length) {
					modules = sortModules(entryModule);
				}

				var bundle = {
					entryModule: entryModule,
					modules: modules,
					externalModules: externalModules,
					names: names
				};

				resolveChains(modules, moduleLookup);
				combine(bundle);

				return bundle;
			});
		});
	}, function (err) {
		if (err.code === 'ENOENT') {
			throw new Error('Could not find entry module (' + entry + ')');
		}

		throw err;
	});

	function fetchModule(moduleId, absolutePath) {
		if (!hasOwnProp.call(promiseByPath, absolutePath)) {
			promiseByPath[absolutePath] = (hasOwnProp.call(userModules, absolutePath) ? Promise.resolve(userModules[absolutePath]) : readFile(absolutePath).then(String)).then(function (source) {
				var code = undefined,
				    ast = undefined;

				// normalise
				if (typeof source === 'object') {
					code = source.code;
					ast = source.ast;
				} else {
					code = source;
					ast = null;
				}

				if (options.transform) {
					code = options.transform(code, absolutePath);

					if (typeof code !== 'string' && !isThenable(code)) {
						throw new Error('transform should return String or Promise');
					}
				}

				var module = getModule({
					id: moduleId,
					path: absolutePath,
					code: code,
					ast: ast
				});

				moduleLookup[moduleId] = module;

				return promiseSequence(module.imports, function (x) {
					var id = resolveId(x.path, module.path).replace(base.replace(/\\/g, '/'), '');

					if (id === moduleId) {
						throw new Error('A module (' + moduleId + ') cannot import itself');
					}

					// Some modules can be skipped
					if (skip && ~skip.indexOf(id)) {
						var skippedModule = {
							id: id,
							isSkipped: true
						};

						x.module = skippedModule;
						return skippedModule;
					}

					return resolvePath(base, userModules, id, absolutePath, options.resolvePath).then(function (absolutePath) {
						var promise = hasOwnProp.call(promiseByPath, absolutePath) && promiseByPath[absolutePath];
						var cyclical = !!promise;

						if (cyclical) {
							// ensure all modules are set before we
							// create the bundle...
							cyclicalModules.push(promise.then(function (module) {
								return x.module = module;
							}));

							// ...then short-circuit
							return;
						}

						return fetchModule(id, absolutePath).then(function (module) {
							return x.module = module;
						});
					}, function handleError(err) {
						if (err.code === 'ENOENT') {
							// Most likely an external module
							var externalModule = hasOwnProp.call(externalModuleLookup, id) && externalModuleLookup[id];

							if (!externalModule) {
								externalModule = {
									id: id,
									isExternal: true
								};

								externalModules.push(externalModule);
								externalModuleLookup[id] = externalModule;
							}

							x.module = externalModule;
						} else {
							throw err;
						}
					});
				}).then(function () {
					return modules.push(module);
				}).then(function () {
					return module;
				});
			});
		}

		return promiseByPath[absolutePath];
	}
}

function resolvePath(base, userModules, moduleId, importerPath, resolver) {
	var noExt = moduleId.replace(/\.js$/, '');

	return tryPath(base, noExt + '.js', userModules)['catch'](function () {
		return tryPath(base, noExt + sep + 'index.js', userModules);
	})['catch'](function (err) {
		var resolvedPromise = resolver && Promise.resolve(resolver(moduleId, importerPath));

		if (resolvedPromise) {
			return resolvedPromise.then(function (resolvedPath) {
				if (!resolvedPath) {
					// hack but whatevs, it saves handling real ENOENTs differently
					var _err = new Error();
					_err.code = 'ENOENT';
					throw _err;
				}

				return stat(resolvedPath).then(function () {
					return resolve(base, resolvedPath);
				});
			});
		} else {
			throw err;
		}
	});
}

function tryPath(base, filename, userModules) {
	var absolutePath = resolve(base, filename);

	if (hasOwnProp.call(userModules, absolutePath)) {
		return Promise.resolve(absolutePath);
	}
	return stat(absolutePath).then(function () {
		return absolutePath;
	});
}

function isThenable(obj) {
	return obj && typeof obj.then === 'function';
}
//# sourceMappingURL=/www/ESPERANTO/esperanto/.gobble-build/01-babel/1/bundler/getBundle.js.01-babel.map